import { Link } from "react-router-dom";
import "./DashboardV2.css";

/**
 * Dashboard mockup — what a creator sees when logged in. Real data wiring
 * comes with the Stripe Subscriptions pass; for v1 this is hardcoded so
 * the layout and information design can be reviewed.
 */
export default function DashboardV2(): JSX.Element {
  // Mock supporter list — what the creator's wall holds today.
  const supporters = [
    { name: "James Lockwood", started: "Jun 2023", monthly: 250, total: 9000, tier: "Architect", style: "Engraved" },
    { name: "Helena Voss",    started: "Dec 2023", monthly: 100, total: 3000, tier: "Founder",   style: "Gilded" },
    { name: "M.J. Asante",    started: "Apr 2024", monthly:  50, total: 1300, tier: "Benefactor",style: "Gilded" },
    { name: "Dr. Owen Hayes", started: "May 2025", monthly:  25, total:  325, tier: "Patron",    style: "Signature" },
    { name: "Camille Joubert",started: "Jun 2025", monthly:  25, total:  300, tier: "Patron",    style: "Signature" },
    { name: "Hiro Tanaka",    started: "Aug 2025", monthly:  10, total:  100, tier: "Member",    style: "Inked" },
    { name: "Lena R.",        started: "Aug 2025", monthly:  10, total:  100, tier: "Member",    style: "Inked" },
    { name: "Sarah K.",       started: "Sep 2025", monthly:   5, total:   45, tier: "Supporter", style: "Card" },
    { name: "Marcus T.",      started: "Sep 2025", monthly:   5, total:   45, tier: "Supporter", style: "Card" },
    { name: "Priya N.",       started: "Oct 2025", monthly:   5, total:   40, tier: "Supporter", style: "Card" },
  ];

  const mrr = supporters.reduce((s, x) => s + x.monthly, 0);
  const totalLifetime = supporters.reduce((s, x) => s + x.total, 0);
  const fee = Math.round(mrr * 0.049);
  const takeHome = mrr - fee;

  return (
    <div className="at-page dbg">
      {/* Side nav */}
      <aside className="dbg-side">
        <Link to="/" className="dbg-brand">
          <span className="dbg-brand-mark">◆</span> SubsWall
        </Link>
        <nav>
          <a className="dbg-nav-link dbg-active" href="#">Home</a>
          <a className="dbg-nav-link" href="#">Supporters <span>{supporters.length}</span></a>
          <a className="dbg-nav-link" href="#">Earnings</a>
          <a className="dbg-nav-link" href="#">Wall settings</a>
          <a className="dbg-nav-link" href="#">Payouts</a>
          <a className="dbg-nav-link" href="#">Embed</a>
          <div className="dbg-nav-divider"></div>
          <a className="dbg-nav-link" href="#">Account</a>
          <a className="dbg-nav-link" href="#">Sign out</a>
        </nav>
        <div className="dbg-side-foot">
          <Link to="/demo" className="dbg-side-foot-link">View your public wall ↗</Link>
        </div>
      </aside>

      {/* Main */}
      <main className="dbg-main">
        <header className="dbg-head">
          <div>
            <div className="at-eyebrow">Welcome back, Demo</div>
            <h1 className="at-display dbg-h1">Your wall.</h1>
          </div>
          <Link to="/demo" className="at-btn at-btn-ghost">View public wall →</Link>
        </header>

        {/* KPI cards */}
        <section className="dbg-kpis">
          <div className="dbg-kpi">
            <div className="at-eyebrow">Monthly recurring</div>
            <div className="dbg-kpi-num at-mono">${mrr.toLocaleString()}</div>
            <div className="dbg-kpi-sub">${takeHome.toLocaleString()} to you · ${fee.toLocaleString()} fee</div>
          </div>
          <div className="dbg-kpi">
            <div className="at-eyebrow">Supporters</div>
            <div className="dbg-kpi-num at-mono">{supporters.length}</div>
            <div className="dbg-kpi-sub">+2 this month · 0 cancellations</div>
          </div>
          <div className="dbg-kpi">
            <div className="at-eyebrow">Lifetime contributed</div>
            <div className="dbg-kpi-num at-mono">${totalLifetime.toLocaleString()}</div>
            <div className="dbg-kpi-sub">Across all supporters · since Jun 2023</div>
          </div>
          <div className="dbg-kpi dbg-kpi-gold">
            <div className="at-eyebrow">Next payout</div>
            <div className="dbg-kpi-num at-mono">${takeHome.toLocaleString()}</div>
            <div className="dbg-kpi-sub">Stripe · 1st of next month</div>
          </div>
        </section>

        {/* Supporters table */}
        <section className="dbg-section">
          <div className="dbg-section-head">
            <h2 className="at-display dbg-h2">Your supporters</h2>
            <div className="dbg-section-tools">
              <input type="search" placeholder="Search by name…" />
              <button className="at-btn at-btn-ghost">Export CSV</button>
            </div>
          </div>
          <table className="dbg-table">
            <thead>
              <tr>
                <th>Name</th>
                <th>Since</th>
                <th>Monthly</th>
                <th>Lifetime</th>
                <th>Tier</th>
                <th>Style</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {supporters.map((s) => (
                <tr key={s.name}>
                  <td>{s.name}</td>
                  <td className="at-mono">{s.started}</td>
                  <td className="at-mono">${s.monthly}</td>
                  <td className="at-mono">${s.total.toLocaleString()}</td>
                  <td><span className={`dbg-tier dbg-tier-${s.tier.toLowerCase()}`}>{s.tier}</span></td>
                  <td className="dbg-mute">{s.style}</td>
                  <td className="dbg-row-actions">
                    <button>Message</button>
                    <button>Pin</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </section>

        {/* Embed snippet */}
        <section className="dbg-section">
          <div className="dbg-section-head">
            <h2 className="at-display dbg-h2">Embed your wall</h2>
          </div>
          <p className="at-lead dbg-embed-copy">
            Paste this anywhere — your YouTube channel About page, video descriptions,
            your own site footer. The wall updates live as new supporters sign on.
          </p>
          <pre className="dbg-embed-code at-mono">{`<iframe src="https://subswall.com/demo/embed"
        width="100%" height="600"
        style="border:0;border-radius:14px;"
        loading="lazy"></iframe>`}</pre>
        </section>
      </main>
    </div>
  );
}
